import { __rest } from "tslib";
import * as React from "react";
import { ChevronDown } from "lucide-react";
import { MoreMenu } from "../../components/patterns/MoreMenu";
import { cn } from "../../lib/utils";
const TableContext = React.createContext(undefined);
const TableSectionContext = React.createContext(undefined);
const TableRowContext = React.createContext(undefined);
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;
function getColumnIndex(cell) {
    let column = 0;
    let sibling = cell.previousElementSibling;
    while (sibling) {
        column += sibling.colSpan || 1;
        sibling = sibling.previousElementSibling;
    }
    return column;
}
function useColumnIndex(ref) {
    const [column, setColumn] = React.useState(-1);
    useIsomorphicLayoutEffect(() => {
        if (ref.current) {
            setColumn(getColumnIndex(ref.current));
        }
    });
    return column;
}
function useMergedRef(forwardedRef) {
    const innerRef = React.useRef(null);
    React.useImperativeHandle(forwardedRef, () => innerRef.current);
    return innerRef;
}
const Table = React.forwardRef((_a, ref) => {
    var { className, responsive = false } = _a, props = __rest(_a, ["className", "responsive"]);
    const [columns, setColumns] = React.useState({});
    // Measuring columns and collecting their infos happens in layout effects,
    // i.e., before the first paint. Until then the collapsed table stays
    // invisible.
    const [ready, setReady] = React.useState(false);
    useIsomorphicLayoutEffect(() => {
        setReady(true);
    }, []);
    const setColumn = React.useCallback((column, span, info) => setColumns((current) => {
        let changed = false;
        const next = Object.assign({}, current);
        for (let i = column; i < column + span; i++) {
            const existing = current[i];
            if (!existing ||
                existing.label !== info.label ||
                existing.primary !== info.primary ||
                existing.noDetail !== info.noDetail ||
                existing.isActions !== info.isActions) {
                next[i] = info;
                changed = true;
            }
        }
        return changed ? next : current;
    }), []);
    const removeColumn = React.useCallback((column, span) => setColumns((current) => {
        let changed = false;
        const next = Object.assign({}, current);
        for (let i = column; i < column + span; i++) {
            if (i in next) {
                delete next[i];
                changed = true;
            }
        }
        return changed ? next : current;
    }), []);
    const context = React.useMemo(() => {
        var _a, _b;
        let fallbackColumn = 0;
        while (((_a = columns[fallbackColumn]) === null || _a === void 0 ? void 0 : _a.noDetail) ||
            ((_b = columns[fallbackColumn]) === null || _b === void 0 ? void 0 : _b.isActions)) {
            fallbackColumn++;
        }
        const hasPrimary = Object.values(columns).some((info) => info.primary && !info.isActions);
        return {
            responsive,
            columns,
            fallbackColumn,
            hasPrimary,
            setColumn,
            removeColumn,
        };
    }, [responsive, columns, setColumn, removeColumn]);
    return (React.createElement(TableContext.Provider, { value: context },
        React.createElement("div", { className: "relative w-full overflow-auto" },
            React.createElement("table", Object.assign({ ref: ref, className: cn("w-full caption-bottom text-sm", responsive &&
                    "max-md:flex max-md:flex-col max-md:[&>caption]:order-last", responsive && !ready && "max-md:invisible", className) }, props)))));
});
Table.displayName = "Table";
const TableHeader = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const table = React.useContext(TableContext);
    return (React.createElement(TableSectionContext.Provider, { value: "header" },
        React.createElement("thead", Object.assign({ ref: ref, className: cn("[&_tr]:border-b", (table === null || table === void 0 ? void 0 : table.responsive) && "max-md:hidden", className) }, props))));
});
TableHeader.displayName = "TableHeader";
const TableBody = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const table = React.useContext(TableContext);
    return (React.createElement(TableSectionContext.Provider, { value: "body" },
        React.createElement("tbody", Object.assign({ ref: ref, className: cn("[&_tr:last-child]:border-0", (table === null || table === void 0 ? void 0 : table.responsive) && "max-md:block", className) }, props))));
});
TableBody.displayName = "TableBody";
const TableFooter = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const table = React.useContext(TableContext);
    return (React.createElement(TableSectionContext.Provider, { value: "footer" },
        React.createElement("tfoot", Object.assign({ ref: ref, className: cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", (table === null || table === void 0 ? void 0 : table.responsive) &&
                "max-md:block max-md:[&>tr]:flex max-md:[&>tr>td]:flex-1", className) }, props))));
});
TableFooter.displayName = "TableFooter";
const TableRow = React.forwardRef((_a, forwardedRef) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    const ref = useMergedRef(forwardedRef);
    const table = React.useContext(TableContext);
    const section = React.useContext(TableSectionContext);
    const [expanded, setExpanded] = React.useState(false);
    const [actions, setActions] = React.useState(null);
    // A row consisting of a single cell (e.g., an empty state spanning all
    // columns) has nothing to expand and is just shown as is.
    const [single, setSingle] = React.useState(false);
    const responsive = !!(table === null || table === void 0 ? void 0 : table.responsive) && section === "body";
    const row = React.useMemo(() => ({ expanded, setActions }), [expanded]);
    useIsomorphicLayoutEffect(() => {
        if (responsive && ref.current) {
            const cells = Array.from(ref.current.cells).filter((cell) => !cell.hasAttribute("data-row-toggle"));
            setSingle(cells.length === 1);
        }
    });
    if (!responsive) {
        return (React.createElement("tr", Object.assign({ ref: ref, className: cn("border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted", className) }, props), children));
    }
    return (React.createElement(TableRowContext.Provider, { value: single ? undefined : row },
        React.createElement("tr", Object.assign({ ref: ref, "data-expanded": single ? undefined : expanded, className: cn("border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted", single
                ? "max-md:flex max-md:[&>td]:flex-1"
                : "max-md:flex max-md:flex-wrap max-md:items-center max-md:gap-x-2 max-md:px-4", !single && expanded && "max-md:pb-3", className) }, props),
            children,
            !single && (React.createElement("td", { "data-row-toggle": true, className: "order-1 ml-auto shrink-0 py-3 md:hidden" },
                React.createElement("div", { className: "flex items-center gap-1" },
                    actions !== null && actions !== undefined && (React.createElement(MoreMenu, { className: "h-8 w-8" }, actions)),
                    React.createElement("button", { type: "button", "aria-expanded": expanded, "aria-label": expanded ? "Weniger anzeigen" : "Mehr anzeigen", className: "flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", onClick: () => setExpanded((value) => !value) },
                        React.createElement(ChevronDown, { className: cn("h-4 w-4 shrink-0 transition-transform duration-200", expanded && "rotate-180") }))))))));
});
TableRow.displayName = "TableRow";
const TableHead = React.forwardRef((_a, forwardedRef) => {
    var { className, children, label, primary = false, noDetail = false, isActions = false, colSpan } = _a, props = __rest(_a, ["className", "children", "label", "primary", "noDetail", "isActions", "colSpan"]);
    const ref = useMergedRef(forwardedRef);
    const table = React.useContext(TableContext);
    const section = React.useContext(TableSectionContext);
    const column = useColumnIndex(ref);
    const span = colSpan || 1;
    const register = !!(table === null || table === void 0 ? void 0 : table.responsive) && section === "header";
    const setColumn = table === null || table === void 0 ? void 0 : table.setColumn;
    const removeColumn = table === null || table === void 0 ? void 0 : table.removeColumn;
    useIsomorphicLayoutEffect(() => {
        if (register && column >= 0 && setColumn && removeColumn) {
            setColumn(column, span, {
                label: label !== null && label !== void 0 ? label : children,
                primary,
                noDetail,
                isActions,
            });
            return () => removeColumn(column, span);
        }
    }, [
        register,
        column,
        span,
        label,
        children,
        primary,
        noDetail,
        isActions,
        setColumn,
        removeColumn,
    ]);
    return (React.createElement("th", Object.assign({ ref: ref, className: cn("h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0", className), colSpan: colSpan }, props), children));
});
TableHead.displayName = "TableHead";
const TableCell = React.forwardRef((_a, forwardedRef) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    const ref = useMergedRef(forwardedRef);
    const table = React.useContext(TableContext);
    const row = React.useContext(TableRowContext);
    const column = useColumnIndex(ref);
    const info = table === null || table === void 0 ? void 0 : table.columns[column];
    const isActions = !!(info === null || info === void 0 ? void 0 : info.isActions);
    const primary = (table === null || table === void 0 ? void 0 : table.hasPrimary)
        ? !!(info === null || info === void 0 ? void 0 : info.primary)
        : column === (table === null || table === void 0 ? void 0 : table.fallbackColumn);
    const setActions = row === null || row === void 0 ? void 0 : row.setActions;
    // The actions are shown in the row's more menu when collapsed.
    useIsomorphicLayoutEffect(() => {
        if (isActions && setActions) {
            setActions(children);
            return () => setActions(null);
        }
    }, [isActions, children, setActions]);
    if (!row) {
        return (React.createElement("td", Object.assign({ ref: ref, className: cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className) }, props), children));
    }
    if (isActions) {
        return (React.createElement("td", Object.assign({ ref: ref, "data-actions": true, className: cn("p-4 align-middle [&:has([role=checkbox])]:pr-0 max-md:hidden", className) }, props), children));
    }
    if (primary) {
        return (React.createElement("td", Object.assign({ ref: ref, "data-primary": true, className: cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", "max-md:min-w-0 max-md:flex-1 max-md:truncate max-md:px-0 max-md:py-3", className) }, props), children));
    }
    return (React.createElement("td", Object.assign({ ref: ref, className: cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", "max-md:order-2 max-md:basis-full max-md:px-0 max-md:py-1.5", row.expanded && !(info === null || info === void 0 ? void 0 : info.noDetail)
            ? "max-md:grid max-md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] max-md:gap-4"
            : "max-md:hidden", className) }, props),
        React.createElement("div", { className: "font-medium text-muted-foreground md:hidden" }, info === null || info === void 0 ? void 0 : info.label),
        React.createElement("div", { className: "md:contents" }, children)));
});
TableCell.displayName = "TableCell";
const TableCaption = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("caption", Object.assign({ ref: ref, className: cn("mt-4 text-sm text-muted-foreground", className) }, props)));
});
TableCaption.displayName = "TableCaption";
export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption, };
//# sourceMappingURL=table.js.map